import { getFramework, ResourceThumbnail } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { transformFoundationVectorLayerDataToClient } from '@/utils/transform-kit';
import { type ILayerBehavior, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { EdgelessVectorLayer } from '../edgeless';
import { normalizeVectorTextPadding } from '../utils/text';
import { resolveVectorTextLayout } from '../utils/text-layout';

type Behaviors = Pick<
    ILayerBehavior,
    'render' | 'getEdgelessObject' | 'getMenuOverlapName' | 'getMenuOverlapIcon' | 'onBeforeAddOrUpdate'
>;

/**
 * 创建 Vector Layer 的渲染与写入行为。
 *
 * @param identity - 当前编辑器 framework 标识。
 * @returns Vector Layer behaviors。
 */
export const createRendererBehaviors = (identity: string) => {
    const behaviors: Behaviors = {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessVectorLayer(layer.id, getFramework(identity));
        },

        getMenuOverlapName(_: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.vector);
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            const { url } = layer.data as IVectorLayerProps;
            return <ResourceThumbnail resource={resource} fallbackUrl={url} />;
        },

        /**
         * 归一化 Vector runtime 数据，并让 add/update/history 重放复用宿主文字布局。
         *
         * @param layer - 即将写入 Frame 的 Vector Layer。
         * @returns void。
         */
        onBeforeAddOrUpdate(layer) {
            const maybeFoundationLayerData = layer.data as IVectorLayerProps;

            const { textInfo } = maybeFoundationLayerData;
            if (textInfo && textInfo.text) {
                maybeFoundationLayerData.textInfo!.text = maybeFoundationLayerData.textInfo!.text.replace(
                    /[\x00-\x09\x0b-\x1f\x7f-\x9f]/gi,
                    '',
                );
            }

            const runtimeData = layer.__runtime || {};
            if (!runtimeData.isInit) {
                layer.data = transformFoundationVectorLayerDataToClient(maybeFoundationLayerData);
            }

            layer.__runtime = { ...runtimeData, isInit: true };

            // 确保 proactiveWidth 和 proactiveHeight 有值
            if (typeof layer.__proactiveWidth === 'undefined') layer.__proactiveWidth = layer.width;
            if (typeof layer.__proactiveHeight === 'undefined') layer.__proactiveHeight = layer.height;

            const layerData = layer.data as IVectorLayerProps;

            // 初始化 effect
            if (!layerData.effect) {
                layerData.effect = { shadow: [], reflection: [] };
            }

            if (!layerData.effect.reflection) layerData.effect.reflection = [];
            if (!layerData.effect.shadow) layerData.effect.shadow = [];

            // 修正 blur
            if (layerData.blur < 0) layerData.blur = 0;

            // 初始化 borderRadius
            if (!layerData.borderRadius) {
                const radius = layerData.radius;
                layerData.borderRadius = {
                    leftBottom: radius,
                    rightBottom: radius,
                    rightTop: radius,
                    leftTop: radius,
                };
            }

            // 初始化文本信息内边距
            if (layerData.textInfo && !layerData.textInfo.padding) {
                layerData.textInfo.padding = {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                };
            }

            const runtimeTextInfo = layerData.textInfo;
            if (runtimeTextInfo && !runtimeTextInfo.text && runtimeTextInfo.padding) {
                runtimeTextInfo.padding = normalizeVectorTextPadding(
                    {
                        width: layer.__proactiveWidth ?? layer.width,
                        height: layer.__proactiveHeight ?? layer.height,
                    },
                    runtimeTextInfo,
                );
            }

            // History undo/redo 与外部 addOrUpdate 也必须走同一宿主 layout commit，
            // 这样同步 API 在字体 pending 时延后的几何，重放后仍会收敛到相同最终尺寸。
            if (layerData.textInfo) {
                Object.assign(layer, resolveVectorTextLayout(getFramework(identity), layer));
            }
        },
    };

    return behaviors;
};
